'use client';
import { createClient } from '@/lib/supabase/client';
import { useSessionCache } from '@/lib/session/sessionCache';

export type PlanStatus = 'active' | 'paid_in_full' | 'ending' | 'payment_overdue' | 'expired' | 'not_assigned';

export interface MyMembershipPlan {
  route: string | null;
  planName: string | null;
  planDescription: string | null;
  startDate: string | null;
  expiryDate: string | null;
  daysRemaining: number | null;
  status: PlanStatus;
  isStaff: boolean;
}

export interface MyMembershipPlanState {
  loading: boolean;
  plan: MyMembershipPlan | null;
  error: string | null;
}

const KNOWN_STATUSES: PlanStatus[] = ['active', 'paid_in_full', 'ending', 'payment_overdue', 'expired', 'not_assigned'];

function mapRow(row: any): MyMembershipPlan {
  const rawStatus = String(row?.status ?? 'not_assigned');
  const status = (KNOWN_STATUSES.includes(rawStatus as PlanStatus) ? rawStatus : 'not_assigned') as PlanStatus;
  const days = row?.days_remaining;
  return {
    route: row?.route ?? null,
    planName: row?.plan_name ?? null,
    planDescription: row?.plan_description ?? null,
    startDate: row?.start_date ?? null,
    expiryDate: row?.expiry_date ?? null,
    daysRemaining: days === null || days === undefined ? null : Number(days),
    status,
    isStaff: row?.is_staff === true,
  };
}

export async function fetchMyMembershipPlan(): Promise<MyMembershipPlan> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('my_membership_plan');
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  return mapRow(row ?? { status: 'not_assigned' });
}

/** Reads the plan from the shared app context: no network request of its own. */
export function useMyMembershipPlan(): MyMembershipPlanState {
  const snap = useSessionCache();
  if (!snap.ready) return { loading: true, plan: null, error: null };
  return { loading: false, plan: snap.signedIn ? snap.plan : null, error: null };
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function formatPlanDate(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return value;
  return `${Number(m[3])} ${month} ${m[1]}`;
}
